import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from '@/components/ui/drawer';
import { useIsMobile } from '@/components/ui/use-mobile';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
  Building2,
  User,
  Mail,
  Phone,
  DollarSign,
  Save,
  ArrowRight,
  Globe,
  ExternalLink,
  Briefcase,
  Calendar,
  Ghost,
  Info,
} from 'lucide-react';
import {
  Client,
  ClientActivity,
  ClientLifecycleStage,
  LIFECYCLE_STAGES,
  DEAL_TYPES,
  PRIORITY_TIERS,
  getAllowedTransitions,
  DealType,
  RevenueType,
  REVENUE_TYPES,
} from '@/types/crm';
import { projectAPI } from '@/utils/supabase/client';
import { DocumentsPanel } from '@features/documents/components/DocumentsPanel';
import { ClientActivityTab } from './ClientActivityTab';

interface ClientSheetProps {
  client: Client | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: () => void;
}

// Get current month in YYYY-MM format
const getCurrentMonth = () => new Date().toISOString().substring(0, 7);

// 44px touch targets on phones, standard 36px from sm up
const inputSize = 'h-11 sm:h-9';
const selectSize = 'data-[size=default]:h-11 sm:data-[size=default]:h-9';

// Get next 3 months for ad-hoc revenue selection
const getMonthOptions = () => {
  const months = [];
  for (let i = 0; i < 3; i++) {
    const date = new Date();
    date.setMonth(date.getMonth() + i);
    const value = date.toISOString().substring(0, 7);
    const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    months.push({ value, label });
  }
  return months;
};

export function ClientSheet({ client, open, onOpenChange, onUpdate }: ClientSheetProps) {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState<ClientActivity[]>([]);
  const [formData, setFormData] = useState({
    company_name: '',
    primary_contact_name: '',
    primary_contact_email: '',
    primary_contact_phone: '',
    website: '',
    priority_tier: null as number | null,
    deal_type: '' as DealType | '',
    estimated_monthly_value: 0,
    revenue_type: 'recurring' as RevenueType,
    is_ghosty: false,
  });

  // Ad-hoc revenue posts to /crm/clients/:id/ad-hoc-revenue, which is
  // requireAdminOrOwner: realised money is deliberately withheld from team_lead.
  // The panel is hidden for them rather than left visible to 403 on submit --
  // the rest of this sheet is role-blind because every other write it makes is
  // open to a pipeline writer, and this is the one that is not.
  const { isSuperAdmin, isOwner } = usePermissions();
  const canEditRealisedRevenue = isSuperAdmin || isOwner;

  // Ad-hoc revenue state (separate from main form since it saves to different table)
  const [adHocMonth, setAdHocMonth] = useState(getCurrentMonth());
  const [adHocAmount, setAdHocAmount] = useState(0);
  const [savingAdHoc, setSavingAdHoc] = useState(false);

  // Fetch activities when client is selected
  const fetchActivities = async () => {
    if (!client) return;
    try {
      const data = await projectAPI.getClientActivities(client.id);
      setActivities(data);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    }
  };

  useEffect(() => {
    if (client) {
      setFormData({
        company_name: client.company_name,
        primary_contact_name: client.primary_contact_name,
        primary_contact_email: client.primary_contact_email || '',
        primary_contact_phone: client.primary_contact_phone || '',
        website: client.website || '',
        priority_tier: client.priority_tier || null,
        deal_type: client.deal_type || '',
        estimated_monthly_value: client.estimated_monthly_value || 0,
        revenue_type: client.revenue_type || 'recurring',
        is_ghosty: client.is_ghosty || false,
      });
      // Reset ad-hoc state when client changes
      setAdHocMonth(getCurrentMonth());
      setAdHocAmount(0);
      // Fetch activities
      fetchActivities();
    }
  }, [client]);

  const handleSave = async () => {
    if (!client) return;
    setLoading(true);
    try {
      await projectAPI.updateClient(client.id, {
        company_name: formData.company_name,
        primary_contact_name: formData.primary_contact_name,
        primary_contact_email: formData.primary_contact_email || undefined,
        primary_contact_phone: formData.primary_contact_phone || undefined,
        website: formData.website || undefined,
        priority_tier: formData.priority_tier || undefined,
        deal_type: formData.deal_type || undefined,
        estimated_monthly_value: formData.estimated_monthly_value ?? undefined,
        revenue_type: formData.revenue_type,
        is_ghosty: formData.is_ghosty,
      });
      onUpdate();
    } catch (error) {
      console.error('Failed to update client:', error);
      toast.error('Failed to save changes');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAdHocRevenue = async () => {
    if (!client) return;
    setSavingAdHoc(true);
    try {
      await projectAPI.setAdHocRevenue(client.id, adHocMonth, adHocAmount);
      onUpdate();
    } catch (error) {
      console.error('Failed to save ad-hoc revenue:', error);
      toast.error('Failed to save ad-hoc revenue');
    } finally {
      setSavingAdHoc(false);
    }
  };

  const handleTransition = async (toStage: ClientLifecycleStage) => {
    if (!client) return;
    setLoading(true);
    try {
      await projectAPI.transitionClient(client.id, { to_stage: toStage });
      onUpdate();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to transition client:', error);
      toast.error('Failed to move stage');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (!client) return null;

  const stageConfig = LIFECYCLE_STAGES[client.lifecycle_stage];
  const allowedTransitions = getAllowedTransitions(client.lifecycle_stage);

  // Shared header content
  const headerContent = (
    <>
      <div className="flex items-center gap-2">
        <Building2 className="h-6 w-6 text-muted-foreground" />
        {isMobile ? (
          <DrawerTitle>{client.company_name}</DrawerTitle>
        ) : (
          <SheetTitle>{client.company_name}</SheetTitle>
        )}
      </div>
      {isMobile ? (
        <DrawerDescription className="flex items-center gap-2 mt-1">
          <Badge variant="outline">{stageConfig.label}</Badge>
          <span>{stageConfig.description}</span>
        </DrawerDescription>
      ) : (
        <SheetDescription className="flex items-center gap-2 mt-1">
          <Badge variant="outline">{stageConfig.label}</Badge>
          <span>{stageConfig.description}</span>
        </SheetDescription>
      )}
    </>
  );

  // Shared body content (tabs)
  const bodyContent = (
    <Tabs defaultValue="details" className="mt-4">
            <TabsList className="grid w-full grid-cols-4 h-11 sm:h-9">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
              <TabsTrigger value="lifecycle">Lifecycle</TabsTrigger>
              <TabsTrigger value="documents">Docs</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-6 mt-6">
              {/* Company Info */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground pb-1">Company Information</h4>
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input
                      id="company_name"
                      className={inputSize}
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="primary_contact_name">Primary Contact</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="primary_contact_name"
                        className={`pl-9 ${inputSize}`}
                        value={formData.primary_contact_name}
                        onChange={(e) => setFormData({ ...formData, primary_contact_name: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="primary_contact_email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="primary_contact_email"
                          type="email"
                          className={`pl-9 ${inputSize}`}
                          value={formData.primary_contact_email}
                          onChange={(e) => setFormData({ ...formData, primary_contact_email: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="primary_contact_phone">Phone</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="primary_contact_phone"
                          className={`pl-9 ${inputSize}`}
                          value={formData.primary_contact_phone}
                          onChange={(e) => setFormData({ ...formData, primary_contact_phone: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="website">Website</Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="website"
                        type="url"
                        className={`pl-9 ${inputSize}`}
                        placeholder="https://example.com"
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="priority_tier">Priority Tier</Label>
                    <Select
                      value={formData.priority_tier?.toString() || ''}
                      onValueChange={(v) => setFormData({ ...formData, priority_tier: v ? Number(v) : null })}
                    >
                      <SelectTrigger className={selectSize}>
                        <SelectValue placeholder="Select priority tier" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(PRIORITY_TIERS).map(([key, { label, description }]) => (
                          <SelectItem key={key} value={key}>
                            <div>
                              <div>{label}</div>
                              <div className="text-xs text-muted-foreground">{description}</div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                  {/* Ghosty Flag */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="is_ghosty"
                      checked={formData.is_ghosty}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_ghosty: !!checked })}
                    />
                    <Label htmlFor="is_ghosty" className="flex items-center gap-1.5 cursor-pointer">
                      <Ghost className="h-4 w-4" />
                      Ghosty
                    </Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-[240px]">
                        This deal is too close to The Spacestation. Consider it a lead only — do not involve anyone from that side.
                      </TooltipContent>
                    </Tooltip>
                  </div>
              </div>

              <Separator className="my-6" />

              {/* Deal Info */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground pb-1">Deal Information</h4>
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="deal_type">Deal Type</Label>
                    <Select
                      value={formData.deal_type}
                      onValueChange={(v) => setFormData({ ...formData, deal_type: v as DealType })}
                    >
                      <SelectTrigger className={selectSize}>
                        <SelectValue placeholder="Select deal type" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(DEAL_TYPES).map(([key, { label }]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="estimated_monthly_value">
                      {formData.revenue_type === 'one_off' ? 'Deal Value (one-time)' : 'Monthly Value'}
                    </Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="estimated_monthly_value"
                        type="number"
                        className={`pl-9 ${inputSize}`}
                        value={formData.estimated_monthly_value}
                        onChange={(e) => setFormData({ ...formData, estimated_monthly_value: Number(e.target.value) })}
                      />
                    </div>
                  </div>
                  {/* Revenue Type - available at every stage so a pipeline deal can be
                      marked one-off before it ever becomes a client */}
                  <>
                      <Separator className="my-4" />
                      <div className="space-y-2">
                        <Label>Revenue Type</Label>
                        <Select
                          value={formData.revenue_type}
                          onValueChange={(v) => setFormData({ ...formData, revenue_type: v as RevenueType })}
                        >
                          <SelectTrigger className={selectSize}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {(Object.entries(REVENUE_TYPES) as [RevenueType, { label: string; description: string }][]).map(([key, { label, description }]) => (
                              <SelectItem key={key} value={key}>
                                <div>
                                  <div>{label}</div>
                                  <div className="text-xs text-muted-foreground">{description}</div>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Ad-hoc revenue entry - only show for variable revenue type on
                          engaged clients; pipeline deals have no forecast rows yet */}
                      {canEditRealisedRevenue &&
                        formData.revenue_type === 'variable' &&
                        (client.lifecycle_stage === 'active' || client.lifecycle_stage === 'committed') && (
                        <div className="p-4 border rounded-lg space-y-4 mt-4">
                          <div className="flex items-center gap-2 text-sm font-medium">
                            <Calendar className="h-4 w-4" />
                            Ad-Hoc Revenue Entry
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <Label htmlFor="adHocMonth">Month</Label>
                              <Select value={adHocMonth} onValueChange={setAdHocMonth}>
                                <SelectTrigger className={selectSize}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {getMonthOptions().map((m) => (
                                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="adHocAmount">Amount</Label>
                              <div className="relative">
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                  id="adHocAmount"
                                  type="number"
                                  className={`pl-9 ${inputSize}`}
                                  value={adHocAmount}
                                  onChange={(e) => setAdHocAmount(Number(e.target.value))}
                                />
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleSaveAdHocRevenue}
                            disabled={savingAdHoc}
                            className="w-full"
                          >
                            {savingAdHoc ? 'Saving...' : 'Save Ad-Hoc Revenue'}
                          </Button>
                        </div>
                      )}
                  </>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="activity" className="mt-6">
              <ClientActivityTab
                clientId={client.id}
                activities={activities}
                onActivityAdded={fetchActivities}
              />
            </TabsContent>

            <TabsContent value="lifecycle" className="space-y-6 mt-6">
              {/* Current Stage */}
              <div className="p-4 bg-muted rounded-lg">
                <div className="text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-2">Current Stage</div>
                <div className="text-lg font-medium">{stageConfig.label}</div>
                <div className="text-sm text-muted-foreground mt-1">{stageConfig.description}</div>
              </div>

              {/* Portfolio Link for Active Clients */}
              {client.lifecycle_stage === 'active' && client.linkedProject && (
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground pb-1">Financial Details</h4>
                  <Link to={`/?project=${client.linkedProject.legacy_id || client.linkedProject.id}`} onClick={() => onOpenChange(false)}>
                    <Button variant="outline" className="w-full justify-start h-auto py-3">
                      <Briefcase className="h-4 w-4 mr-2 flex-shrink-0" />
                      <div className="text-left flex-1">
                        <div>View in Portfolio</div>
                        <div className="text-muted-foreground text-xs font-normal">
                          Edit financial model, tactical specialists, and kickers
                        </div>
                      </div>
                      <ExternalLink className="h-4 w-4 ml-2 text-muted-foreground" />
                    </Button>
                  </Link>
                </div>
              )}

              {/* Available Transitions */}
              {allowedTransitions.length > 0 && (
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground pb-1">Move to Stage</h4>
                  <div className="grid gap-3">
                    {allowedTransitions.map((stage) => {
                      const targetConfig = LIFECYCLE_STAGES[stage];
                      return (
                        <Button
                          key={stage}
                          variant="outline"
                          className="justify-start h-auto py-3"
                          onClick={() => handleTransition(stage)}
                          disabled={loading}
                        >
                          <ArrowRight className="h-4 w-4 mr-2 flex-shrink-0" />
                          <div className="text-left">
                            <div>{targetConfig.label}</div>
                            <div className="text-muted-foreground text-xs font-normal">
                              {targetConfig.description}
                            </div>
                          </div>
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}

              {allowedTransitions.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-8">
                  No transitions available from this stage
                </div>
              )}
            </TabsContent>

            <TabsContent value="documents" className="mt-6">
              {client && <DocumentsPanel clientId={client.id} />}
            </TabsContent>
          </Tabs>
  );

  // Shared footer content
  const footerContent = (
    <Button onClick={handleSave} disabled={loading} className="w-full h-11 sm:h-9">
      <Save className="h-4 w-4 mr-2" />
      Save Changes
    </Button>
  );

  // Mobile: Bottom drawer
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} direction="bottom">
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="px-4 pt-4 pb-3">
            {headerContent}
          </DrawerHeader>
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            {bodyContent}
          </div>
          <DrawerFooter className="px-4 py-4 border-t">
            {footerContent}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop: Side sheet (wider)
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4">
          {headerContent}
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {bodyContent}
        </div>
        <SheetFooter className="px-6 py-4 border-t">
          {footerContent}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
